import { useParams } from '@reach/router';
import { DashboardPath } from 'appConstants';
import { AccountLink, FiatAccountLink } from 'components/sideMenu/SideMenu';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';

export type PropsUseGetFiatAccountDetails = {
    currency?: string;
    accountId?: number;
};
export const useGetFiatAccountDetails = (
    propsUseGetFiatAccountDetails?: PropsUseGetFiatAccountDetails
) => {
    const params = useParams();
    const routeAccountId = [DashboardPath.ACCOUNTS, DashboardPath.FUNDINGACCOUNTS].includes(
        `/${params.page}` as DashboardPath
    )
        ? params.sub_page
        : null;

    const { customerMenuLinks } = useSelector(selectComponentResources);

    const accountDetails = useMemo(() => {
        const requestedAccountId = propsUseGetFiatAccountDetails?.accountId ?? routeAccountId;
        if (requestedAccountId == null && !propsUseGetFiatAccountDetails?.currency) return undefined;
        const allAccounts = customerMenuLinks?.reduce<AccountLink[]>(
            (prev, curr) => prev.concat(curr?.accounts ?? []),
            []
        );
        const account_details = requestedAccountId != null
            ? allAccounts?.find((account) => account.id === Number(requestedAccountId))
            : allAccounts?.find(
                  (account) =>
                      'currencyCode' in account &&
                      account.currencyCode === propsUseGetFiatAccountDetails?.currency
              );

        return isFiatAccountLink(account_details) ? account_details : undefined;
    }, [routeAccountId, propsUseGetFiatAccountDetails?.accountId, propsUseGetFiatAccountDetails?.currency, customerMenuLinks]);
    return accountDetails;
};
export const useGetFiatAccounts = (currencyCode?: string) => {
    const { customerMenuLinks } = useSelector(selectComponentResources);

    return useMemo(() => {
        const allAccounts = customerMenuLinks?.reduce<AccountLink[]>(
            (prev, curr) => prev.concat(curr?.accounts ?? []),
            []
        );

        return (allAccounts ?? []).filter(
            (account): account is FiatAccountLink =>
                isFiatAccountLink(account) &&
                typeof account.currencyCode === 'string' &&
                (!currencyCode || account.currencyCode === currencyCode)
        );
    }, [customerMenuLinks, currencyCode]);
};
export const useGetFiatAccountForCurrency = (currencyCode?: string) => {
    const { customerMenuLinks } = useSelector(selectComponentResources);
    const accountDetails = useMemo(() => {
        if (!currencyCode) return undefined;
        const allAccounts = customerMenuLinks?.reduce<AccountLink[]>(
            (prev, curr) => prev.concat(curr?.accounts ?? []),
            []
        );
        return allAccounts
            ?.filter(isFiatAccountLink)
            .find((account) => account.currencyCode === currencyCode);
    }, [customerMenuLinks, currencyCode]);
    return accountDetails;
};

const isFiatAccountLink = (link?: AccountLink): link is FiatAccountLink => {
    return !!(link as FiatAccountLink).productDisplayName;
};
